import type {
  GoalScopeFormValues,
  ImpactConfigurationValues,
  LciFormValues,
  PCFRecord,
  PCFRecordData,
  PCFResults,
  PCFStatus,
  ReportExportValues,
  ResultsInterpretationValues
} from '../../../services/interface/pcfTypes';
import {
  createPCFRecord,
  getPCFRecord,
  updatePCFRecord
} from '../../../services/modules/PCF/pcfAPI';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import usePCFStore, {resolveUserId} from '../../../store/pcfStore/pcfStore';
import showMessage from '../../../utils/showMessage';
import {buildDefaultRecordData} from '../constants/pcfDefaultValues';

export type PCFSection =
  | 'goalAndScope'
  | 'lci'
  | 'impactConfiguration'
  | 'results'
  | 'report';

export interface SavePCFRecordParams {
  section: PCFSection;
  sectionValues: unknown;
  status: PCFStatus;
  resultsExtras?: PCFResults;
}

const mergeSectionIntoData = (
  data: PCFRecordData,
  section: PCFSection,
  sectionValues: unknown,
  resultsExtras?: PCFResults
): PCFRecordData => {
  switch (section) {
    case 'goalAndScope':
      return {
        ...data,
        goalAndScope: sectionValues as GoalScopeFormValues
      };
    case 'lci':
      return {...data, lci: sectionValues as LciFormValues};
    case 'impactConfiguration':
      return {
        ...data,
        impactConfiguration: sectionValues as ImpactConfigurationValues
      };
    case 'results':
      return {
        ...data,
        results: {
          ...(resultsExtras ?? data.results ?? {}),
          interpretation: sectionValues as ResultsInterpretationValues
        }
      };
    case 'report':
      return {...data, report: sectionValues as ReportExportValues};
    default:
      return data;
  }
};

export const savePCFRecord = async ({
  section,
  sectionValues,
  status,
  resultsExtras
}: SavePCFRecordParams): Promise<PCFRecord> => {
  const {
    recordId,
    createdBy,
    recordData,
    setRecordFromApi,
    patchRecordData,
    setIsSaving
  } = usePCFStore.getState();
  const wasUpdate = Boolean(recordId);

  setIsSaving(true);

  try {
    const companyId = useCompanyStore.getState().companyId;
    if (!companyId) {
      showMessage({status: 'error', message: 'Company not found. Please try again.'});
      throw new Error('Company not found');
    }

    const baseData =
      recordData.goalAndScope != null ? recordData : buildDefaultRecordData();

    const mergedData = mergeSectionIntoData(
      baseData,
      section,
      sectionValues,
      resultsExtras
    );

    patchRecordData(mergedData);

    const input = {
      status,
      company: companyId,
      data: mergedData
    };

    let record: Awaited<ReturnType<typeof createPCFRecord>>;

    if (recordId) {
      let resolvedCreatedBy = createdBy;

      if (!resolvedCreatedBy) {
        const existingRecord = await getPCFRecord(recordId);
        resolvedCreatedBy = resolveUserId(existingRecord.createdBy);
      }

      if (!resolvedCreatedBy) {
        showMessage({
          status: 'error',
          message: 'Unable to update study. Please reload and try again.'
        });
        throw new Error('PCF createdBy missing');
      }

      record = await updatePCFRecord(recordId, input, resolvedCreatedBy);
    } else {
      record = await createPCFRecord(input);
    }

    setRecordFromApi(record);
    showMessage({
      status: 'success',
      message: wasUpdate
        ? 'PCF study updated successfully.'
        : 'PCF study saved successfully.'
    });

    return record;
  } catch {
    showMessage({
      status: 'error',
      message: 'Failed to save PCF study. Please try again.'
    });
    throw new Error('Failed to save PCF study');
  } finally {
    setIsSaving(false);
  }
};
